import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertTriangle, XCircle, ArrowRight, RefreshCw, Zap, ExternalLink, Shield, Globe } from 'lucide-react';
import AppShell from '../components/AppShell';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() {
  return document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1]
    || localStorage.getItem('session_token') || '';
}

const headers = () => ({ Authorization: `Bearer ${getToken()}` });

function StatusBadge({ status }) {
  const map = {
    not_listed: { label: 'Not Listed', color: '#C67A5C', icon: XCircle },
    verified: { label: 'Verified', color: '#1D6B4F', icon: CheckCircle2 },
    inconsistent: { label: 'Issues Found', color: '#B8985A', icon: AlertTriangle },
    pending_submission: { label: 'Pending', color: '#8C7E6E', icon: RefreshCw },
    fix_submitted: { label: 'Fix Sent', color: '#1D6B4F', icon: CheckCircle2 },
  };
  const s = map[status] || map.not_listed;
  const Icon = s.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: `${s.color}15`, color: s.color }}>
      <Icon size={10} /> {s.label}
    </span>
  );
}

export default function DirectoryAutoFix() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { fetchAudit(); }, []);

  const fetchAudit = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/directories/audit`, { headers: headers() });
      setAudit(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleBulkSubmit = async () => {
    setSubmitting(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/directories/bulk-submit`, {}, { headers: headers() });
      setResult({ type: 'submit', ...res.data });
      await fetchAudit();
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  const handleFixAll = async () => {
    setFixing(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/directories/fix-all`, {}, { headers: headers() });
      setResult({ type: 'fix', ...res.data });
      await fetchAudit();
    } catch (e) { console.error(e); }
    setFixing(false);
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4C9B8', borderTopColor: 'transparent' }} />
        </div>
      </AppShell>
    );
  }

  if (!audit?.has_business) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto py-16 px-6 text-center">
          <p style={{ color: '#5C5347' }}>Complete your business profile first to manage directories.</p>
        </div>
      </AppShell>
    );
  }

  const { directories, missing_count, inconsistency_count, health_score, total_directories, listed_count, total_issues } = audit;

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto py-8 px-4 md:px-6" data-testid="directory-autofix-page">
        {/* Header */}
        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-widest mb-2" style={{ color: '#B8985A' }}>Directory Management</p>
          <h1 className="text-2xl font-light" style={{ color: '#3C3226' }} data-testid="directory-page-title">Directory Auto-Fix</h1>
          <p className="mt-2 text-sm" style={{ color: '#5C5347' }}>Keep your business information consistent across all 15 directories AI engines trust.</p>
        </div>

        {/* Health Score + Actions */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="p-6" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }} data-testid="health-score-card">
            <div className="flex items-center gap-3 mb-3">
              <Shield size={20} style={{ color: health_score >= 80 ? '#1D6B4F' : health_score >= 50 ? '#B8985A' : '#C67A5C' }} />
              <p className="text-xs uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Directory Health</p>
            </div>
            <p className="text-4xl font-light" style={{ color: '#3C3226' }}>{health_score}%</p>
            <p className="text-xs mt-1" style={{ color: '#8C7E6E' }}>{listed_count}/{total_directories} directories active</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="p-6" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }} data-testid="missing-card">
            <div className="flex items-center gap-3 mb-3">
              <Globe size={20} style={{ color: '#C67A5C' }} />
              <p className="text-xs uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Missing Listings</p>
            </div>
            <p className="text-4xl font-light" style={{ color: missing_count > 0 ? '#C67A5C' : '#1D6B4F' }}>{missing_count}</p>
            {missing_count > 0 && (
              <button onClick={handleBulkSubmit} disabled={submitting} className="mt-3 text-xs font-medium uppercase tracking-wider flex items-center gap-1.5 transition-opacity hover:opacity-80 disabled:opacity-50" style={{ color: '#1D6B4F' }} data-testid="bulk-submit-btn">
                {submitting ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
                {submitting ? 'Submitting...' : 'Submit to All'}
              </button>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="p-6" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }} data-testid="inconsistencies-card">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle size={20} style={{ color: '#B8985A' }} />
              <p className="text-xs uppercase tracking-wider" style={{ color: '#8C7E6E' }}>Inconsistencies</p>
            </div>
            <p className="text-4xl font-light" style={{ color: inconsistency_count > 0 ? '#B8985A' : '#1D6B4F' }}>{total_issues}</p>
            {inconsistency_count > 0 && (
              <button onClick={handleFixAll} disabled={fixing} className="mt-3 text-xs font-medium uppercase tracking-wider flex items-center gap-1.5 transition-opacity hover:opacity-80 disabled:opacity-50" style={{ color: '#1D6B4F' }} data-testid="fix-all-btn">
                {fixing ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
                {fixing ? 'Fixing...' : 'One-Click Fix All'}
              </button>
            )}
          </motion.div>
        </div>

        {/* Result Banner */}
        {result && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 flex items-start gap-3" style={{ backgroundColor: 'rgba(29,107,79,0.08)', border: '1px solid rgba(29,107,79,0.2)' }} data-testid="result-banner">
            <CheckCircle2 size={18} style={{ color: '#1D6B4F', flexShrink: 0, marginTop: 2 }} />
            <div>
              <p className="text-sm font-medium" style={{ color: '#1D6B4F' }}>{result.message}</p>
              {result.type === 'submit' && result.submitted?.length > 0 && (
                <p className="text-xs mt-1" style={{ color: '#5C5347' }}>Submitted: {result.submitted.map(s => s.name).join(', ')}</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Directory List */}
        <div className="space-y-2" data-testid="directory-list">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium uppercase tracking-wider" style={{ color: '#3C3226' }}>All Directories</h2>
            <button onClick={fetchAudit} className="text-xs flex items-center gap-1" style={{ color: '#8C7E6E' }}>
              <RefreshCw size={12} /> Refresh
            </button>
          </div>

          {directories?.map((dir, i) => (
            <motion.div key={dir.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="p-4 flex items-center justify-between" style={{ backgroundColor: '#FDFBF7', border: '1px solid #E8E2D8' }} data-testid={`dir-row-${dir.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium truncate" style={{ color: '#3C3226' }}>{dir.name}</p>
                  <span className="text-[9px] uppercase px-1.5 py-0.5 rounded" style={{ backgroundColor: dir.category === 'essential' ? 'rgba(29,107,79,0.08)' : 'rgba(140,126,110,0.1)', color: dir.category === 'essential' ? '#1D6B4F' : '#8C7E6E' }}>{dir.category}</span>
                </div>
                {dir.issues?.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {dir.issues.slice(0, 2).map((issue, j) => (
                      <p key={j} className="text-[11px]" style={{ color: '#C67A5C' }}>
                        {issue.field}: "{issue.found || 'missing'}" should be "{issue.expected}"
                      </p>
                    ))}
                    {dir.issues.length > 2 && <p className="text-[10px]" style={{ color: '#8C7E6E' }}>+{dir.issues.length - 2} more</p>}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 ml-4">
                <StatusBadge status={dir.status} />
                {dir.url && (
                  <a href={dir.url} target="_blank" rel="noopener noreferrer" className="text-xs hover:opacity-70" style={{ color: '#B8985A' }}>
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
